import React, { useEffect, useMemo, useState } from "react";
import RowList from "../../../components/common/rowList";
import DeviceLogChart from "../../../components/devices/deviceLogChart";
import useDarkMode from "../../../hooks/useDarkMode";

const SENSOR_TYPE_LABELS = {
  temp_hum: "Temperature / Humidity",
  dio_hub: "Digital I/O Hub",
};

const SENSOR_TYPE_COLORS = {
  temp_hum: "#2563eb",
  dio_hub: "#f97316",
  default: "#64748b",
};

const TEMP_HUM_LOG_METRIC_CONFIGS = {
  temperatureF: {
    metricKey: "temperatureF",
    metricLabel: "Temperature",
    optionLabel: "Temperature (F)",
    title: "Temperature History",
    unit: "F",
    color: "#dc2626",
  },
  humidityPct: {
    metricKey: "humidityPct",
    metricLabel: "Humidity",
    optionLabel: "Humidity",
    title: "Humidity History",
    unit: "% RH",
    color: "#0ea5e9",
  },
};

export default function DevicePageSensors({
  device,
  sensors,
  sensorsLoading,
  sensorsLoaded,
  resources = {},
}) {
  const dark = useDarkMode();
  const resourceSensors = resources.sensors;
  const resourceDevice = resources.device;
  const resourceDevices = resources.devices;
  const normalizedSensors = useMemo(
    () =>
      Array.isArray(sensors)
        ? sensors
        : Array.isArray(resourceSensors)
        ? resourceSensors
        : [],
    [sensors, resourceSensors],
  );
  const resolvedDevice = useMemo(() => {
    if (device) {
      return device;
    }

    const serial =
      normalizedSensors[0]?.enclosureSerialNumber ??
      resourceDevice?.enclosureSerialNumber;
    if (!serial || !Array.isArray(resourceDevices)) {
      return resourceDevice || {};
    }

    return (
      resourceDevices.find(
        (item) => String(item?.enclosureSerialNumber) === String(serial),
      ) ||
      resourceDevice ||
      {}
    );
  }, [device, normalizedSensors, resourceDevice, resourceDevices]);
  const resolvedSensorsLoading =
    sensorsLoading ?? resources.sensorsLoading ?? false;
  const resolvedSensorsLoaded =
    sensorsLoaded ?? resources.sensorsLoaded ?? normalizedSensors.length > 0;
  const summaryTiles = useMemo(() => {
    const tempHumSensors = normalizedSensors.filter(
      (sensor) => sensor?.sensorType === "temp_hum",
    );
    const dioHubSensors = normalizedSensors.filter(
      (sensor) => sensor?.sensorType === "dio_hub",
    );

    const warmestSensor = tempHumSensors.reduce((currentWarmest, sensor) => {
      const reading = Number(sensor?.sensorTempInF);
      if (!Number.isFinite(reading)) return currentWarmest;
      if (!currentWarmest) return sensor;
      return Number(currentWarmest.sensorTempInF) > reading
        ? currentWarmest
        : sensor;
    }, null);

    const averageHumidity = averageOf(
      tempHumSensors.map((sensor) => sensor?.sensorHumidity),
    );

    return [
      {
        label: "Sensors Attached",
        value: String(normalizedSensors.length),
        color: "#2563eb",
      },
      tempHumSensors.length > 0
        ? {
            label: "Temp / Hum Sensors",
            value: String(tempHumSensors.length),
            color: "#10b981",
          }
        : null,
      warmestSensor
        ? {
            label: "Warmest Reading",
            value: formatNumber(warmestSensor.sensorTempInF, 1),
            unit: "F",
            color: "#dc2626",
          }
        : null,
      Number.isFinite(averageHumidity)
        ? {
            label: "Average Humidity",
            value: formatNumber(averageHumidity, 1),
            unit: "% RH",
            color: "#0ea5e9",
          }
        : null,
      dioHubSensors.length > 0
        ? {
            label: "Digital I/O Hubs",
            value: String(dioHubSensors.length),
            color: "#f97316",
          }
        : null,
    ].filter(Boolean);
  }, [normalizedSensors]);

  const isInitialLoading =
    (Boolean(resolvedSensorsLoading) && normalizedSensors.length === 0) ||
    (!resolvedSensorsLoaded && normalizedSensors.length === 0);

  if (isInitialLoading) {
    return <SensorsPageSkeleton />;
  }

  return (
    <div className="grid-container fluid">
      {summaryTiles.length > 0 ? (
        <div style={SUMMARY_GRID_STYLE} className="device-sensor-summary-grid">
          {summaryTiles.map((tile) => (
            <SummaryTile key={tile.label} dark={dark} {...tile} />
          ))}
        </div>
      ) : null}
      {normalizedSensors.length === 0 ? (
        <EmptyState dark={dark} />
      ) : (
        normalizedSensors.map((sensor, index) => (
          <SensorCard
            key={sensor?.sensorId || `${sensor?.sensorPort || "sensor"}-${index}`}
            sensor={sensor}
            index={index}
            dark={dark}
            device={resolvedDevice}
          />
        ))
      )}
    </div>
  );
}

function SensorCard({ sensor, index, dark, device }) {
  const accentColor = sensorAccentColor(sensor?.sensorType);
  const metrics = sensorMetricChips(sensor);
  const currentRows = sensorCurrentRows(sensor);
  const detailRows = sensorDetailRows(sensor, device);
  const serialNumber =
    sensor?.enclosureSerialNumber ??
    device?.enclosureSerialNumber ??
    "";
  const supportsHistoryChart = Boolean(
    sensor?.sensorType === "temp_hum" &&
      serialNumber &&
      sensor?.sensorId,
  );
  const availableLogMetricKeys = useMemo(
    () => (supportsHistoryChart ? Object.keys(TEMP_HUM_LOG_METRIC_CONFIGS) : []),
    [supportsHistoryChart],
  );
  const [selectedLogMetricKey, setSelectedLogMetricKey] = useState(
    availableLogMetricKeys[0] || "temperatureF",
  );

  useEffect(() => {
    const fallbackMetricKey = availableLogMetricKeys[0] || "temperatureF";
    setSelectedLogMetricKey((currentMetricKey) =>
      availableLogMetricKeys.includes(currentMetricKey)
        ? currentMetricKey
        : fallbackMetricKey,
    );
  }, [sensor?.sensorId, availableLogMetricKeys]);

  const activeLogMetric =
    TEMP_HUM_LOG_METRIC_CONFIGS[selectedLogMetricKey] ||
    TEMP_HUM_LOG_METRIC_CONFIGS.temperatureF;
  const sensorIdentifier = buildSensorIdentifier(sensor, device, index);
  const historyChartControls =
    supportsHistoryChart && availableLogMetricKeys.length > 1 ? (
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.35rem",
          fontSize: "0.85rem",
          color: "#374151",
        }}
      >
        <span style={{ fontWeight: 500 }}>Metric</span>
        <select
          aria-label={`Sensor metric for ${sensorLabel(sensor, index)}`}
          value={selectedLogMetricKey}
          onChange={(event) => setSelectedLogMetricKey(event.target.value)}
          style={{
            margin: 0,
            minWidth: "160px",
            padding: "0.35rem 0.5rem",
            borderRadius: "4px",
            border: "1px solid #d7dde2",
            backgroundColor: "#fff",
          }}
        >
          {availableLogMetricKeys.map((metricKey) => {
            const metric = TEMP_HUM_LOG_METRIC_CONFIGS[metricKey];
            return (
              <option key={metricKey} value={metricKey}>
                {metric?.optionLabel || metricKey}
              </option>
            );
          })}
        </select>
      </label>
    ) : null;

  return (
    <section style={SENSOR_CARD_STYLE.wrap(dark)} className="device-sensor-card">
      <div style={SENSOR_CARD_STYLE.header}>
        <div style={{ minWidth: 0 }}>
          <span
            style={{
              ...SENSOR_CARD_STYLE.title(dark),
              borderLeft: `4px solid ${accentColor}`,
            }}
          >
            {sensorLabel(sensor, index)}
          </span>
        </div>
      </div>
      {metrics.length > 0 ? (
        <div style={SENSOR_CARD_STYLE.metricRow}>
          {metrics.map((metric, metricIndex) => (
            <MetricChip
              key={`${sensor?.sensorId || sensor?.sensorPort || index}-${metric.label}-${metricIndex}`}
              dark={dark}
              {...metric}
            />
          ))}
        </div>
      ) : null}

      <div className="grid-x grid-padding-y grid-padding-x">
        <div className="cell large-6">
          <RowList
            rows={[
              {
                label: "Current Readings",
                labelStyle: { fontWeight: "bold", fontSize: "110%" },
              },
              ...currentRows,
            ]}
          />
        </div>
        <div className="cell large-6">
          <RowList
            rows={[
              {
                label: "Sensor Details",
                labelStyle: { fontWeight: "bold", fontSize: "110%" },
              },
              ...detailRows,
            ]}
          />
        </div>
      </div>
      {supportsHistoryChart ? (
        <div className="grid-x grid-padding-x grid-padding-y">
          <div className="cell large-12">
            <DeviceLogChart
              title={activeLogMetric.title}
              subtitle={sensorIdentifier}
              logType="sensors"
              enclosureSerialNumber={serialNumber}
              resourceId={sensor.sensorId}
              metricKey={activeLogMetric.metricKey}
              metricLabel={activeLogMetric.metricLabel}
              unit={activeLogMetric.unit}
              color={activeLogMetric.color}
              controls={historyChartControls}
            />
          </div>
        </div>
      ) : null}
    </section>
  );
}

function SummaryTile({ label, value, unit, color, dark }) {
  return (
    <div style={SUMMARY_TILE_STYLE.wrap(dark)} className="devices-summary-tile">
      <div style={SUMMARY_TILE_STYLE.big(dark)}>
        <span>{value}</span>
        {unit ? <span style={SUMMARY_TILE_STYLE.unit(dark)}>{unit}</span> : null}
      </div>
      <div style={SUMMARY_TILE_STYLE.label(dark)}>{label}</div>
      <div style={{ ...SUMMARY_TILE_STYLE.accent, background: color, width: "100%" }} />
    </div>
  );
}

function MetricChip({ label, value, unit, dark, color }) {
  return (
    <div
      style={{
        ...METRIC_CHIP_STYLE.wrap(dark),
        borderTop: `3px solid ${color}`,
      }}
    >
      <div style={METRIC_CHIP_STYLE.value(dark)}>
        <span>{value}</span>
        {unit ? <span style={METRIC_CHIP_STYLE.unit(dark)}>{unit}</span> : null}
      </div>
      <div style={METRIC_CHIP_STYLE.label(dark)}>{label}</div>
    </div>
  );
}

function EmptyState({ dark }) {
  return (
    <div style={EMPTY_STATE_STYLE.wrap(dark)}>
      <div style={EMPTY_STATE_STYLE.title(dark)}>No sensors found</div>
      <div style={EMPTY_STATE_STYLE.description(dark)}>
        This device is not currently reporting any attached sensor accessories.
      </div>
    </div>
  );
}

function SensorsPageSkeleton() {
  const dark = useDarkMode();

  return (
    <div
      className="grid-container fluid"
      role="status"
      aria-label="Loading sensor data"
      aria-live="polite"
    >
      <div style={SUMMARY_GRID_STYLE} className="device-sensor-summary-grid">
        {SENSOR_SUMMARY_SKELETON_TILES.map((tile) => (
          <SummarySkeletonTile
            key={tile.key}
            dark={dark}
            accentColor={tile.color}
            labelWidth={tile.labelWidth}
            valueWidth={tile.valueWidth}
          />
        ))}
      </div>
      {SENSOR_CARD_SKELETONS.map(({ key, ...card }) => (
        <SensorSkeletonCard key={key} dark={dark} {...card} />
      ))}
    </div>
  );
}

function SummarySkeletonTile({ dark, accentColor, labelWidth, valueWidth }) {
  return (
    <div style={SUMMARY_TILE_STYLE.wrap(dark)} className="devices-summary-tile">
      <SkeletonBlock
        width={valueWidth}
        height={30}
        style={{ borderRadius: 10 }}
      />
      <SkeletonBlock
        width={labelWidth}
        height={12}
        style={{ marginTop: 10, opacity: dark ? 0.55 : 0.82 }}
      />
      <div
        style={{
          ...SUMMARY_TILE_STYLE.accent,
          background: accentColor,
          opacity: dark ? 0.45 : 0.7,
          width: "100%",
        }}
      />
    </div>
  );
}

function SensorSkeletonCard({
  dark,
  titleWidth,
  typeWidth,
  metricChips = [],
  readingRows = [],
  detailRows = [],
}) {
  return (
    <section
      style={SENSOR_CARD_STYLE.wrap(dark)}
      className="device-sensor-card"
      aria-hidden="true"
    >
      <div style={SENSOR_CARD_STYLE.header}>
        <div style={{ display: "grid", gap: 12, flex: "1 1 260px" }}>
          <SkeletonBlock
            width={titleWidth}
            height={28}
            style={{ borderRadius: 10 }}
          />
          <SkeletonBlock
            width={typeWidth}
            height={12}
            style={{ opacity: dark ? 0.55 : 0.74 }}
          />
        </div>
      </div>
      {metricChips.length > 0 ? (
        <div style={SENSOR_CARD_STYLE.metricRow}>
          {metricChips.map((metric, index) => (
            <SkeletonMetricChip
              key={`${metric.color}-${index}`}
              dark={dark}
              {...metric}
            />
          ))}
        </div>
      ) : null}
      <div className="grid-x grid-padding-y grid-padding-x">
        <div className="cell large-6">
          <SkeletonDetailPanel
            dark={dark}
            titleWidth="36%"
            rows={readingRows}
          />
        </div>
        <div className="cell large-6">
          <SkeletonDetailPanel
            dark={dark}
            titleWidth="32%"
            rows={detailRows}
          />
        </div>
      </div>
    </section>
  );
}

function SkeletonMetricChip({ dark, color, valueWidth, labelWidth }) {
  return (
    <div
      style={{
        ...METRIC_CHIP_STYLE.wrap(dark),
        borderTop: `3px solid ${color}`,
      }}
    >
      <SkeletonBlock width={valueWidth} height={22} />
      <SkeletonBlock
        width={labelWidth}
        height={11}
        style={{ marginTop: 10, opacity: dark ? 0.55 : 0.78 }}
      />
    </div>
  );
}

function SkeletonDetailPanel({ dark, titleWidth, rows }) {
  return (
    <div style={SKELETON_DETAIL_STYLE.panel(dark)}>
      <div style={SKELETON_DETAIL_STYLE.header(dark)}>
        <SkeletonBlock width={titleWidth} height={16} />
      </div>
      {rows.map((row, index) => (
        <div
          key={`${row.labelWidth}-${row.valueWidth}-${index}`}
          style={SKELETON_DETAIL_STYLE.row(dark, index)}
        >
          <SkeletonBlock
            width={row.labelWidth}
            height={12}
            style={{ opacity: dark ? 0.48 : 0.72 }}
          />
          <SkeletonBlock width={row.valueWidth} height={14} />
        </div>
      ))}
    </div>
  );
}

function SkeletonBlock({ width, height = 14, style = {} }) {
  return (
    <div
      className="skeleton-blank"
      style={{
        width,
        height,
        minHeight: height,
        margin: 0,
        padding: 0,
        display: "block",
        ...style,
      }}
    />
  );
}

function sensorMetricChips(sensor) {
  if (!sensor) return [];

  if (sensor.sensorType === "temp_hum") {
    return [
      {
        label: "Temperature",
        value: formatNumber(sensor.sensorTempInF, 1),
        unit: "F",
        color: "#dc2626",
      },
      {
        label: "Temperature",
        value: formatNumber(sensor.sensorTempInC, 1),
        unit: "C",
        color: "#2563eb",
      },
      {
        label: "Humidity",
        value: formatNumber(sensor.sensorHumidity, 1),
        unit: "% RH",
        color: "#0ea5e9",
      },
    ];
  }

  if (sensor.sensorType === "dio_hub") {
    return [
      {
        label: "Digital Inputs",
        value: String((sensor.digitalInputs || []).length),
        color: "#10b981",
      },
      {
        label: "Output Relays",
        value: String((sensor.outputRelays || []).length),
        color: "#f97316",
      },
    ];
  }

  return [];
}

function sensorCurrentRows(sensor) {
  if (!sensor) return [{ label: "Status", value: "-" }];

  if (sensor.sensorType === "dio_hub") {
    return [
      {
        label: "Digital Inputs",
        value: String((sensor.digitalInputs || []).length),
      },
      {
        label: "Output Relays",
        value: String((sensor.outputRelays || []).length),
      },
      {
        label: "Input Names",
        value: joinNames((sensor.digitalInputs || []).map((input) => input?.digitalInputName)),
      },
      {
        label: "Relay Names",
        value: joinNames((sensor.outputRelays || []).map((relay) => relay?.relayName)),
      },
    ];
  }

  return [
    {
      label: "Temperature (C)",
      value: formatNumber(sensor.sensorTempInC, 2),
    },
    {
      label: "Temperature (F)",
      value: formatNumber(sensor.sensorTempInF, 2),
    },
    {
      label: "Humidity",
      value: valueWithUnit(sensor.sensorHumidity, "% RH", 2),
    },
  ];
}

function sensorDetailRows(sensor, device) {
  return [
    { label: "Sensor Name", value: sensor?.sensorName || sensorLabel(sensor) },
    { label: "Sensor Type", value: sensorTypeLabel(sensor?.sensorType) },
    { label: "Port", value: sensor?.sensorPort || "-" },
    { label: "Sensor ID", value: sensor?.sensorId || "-" },
    {
      label: "Enclosure Serial Number",
      value:
        sensor?.enclosureSerialNumber ??
        device?.enclosureSerialNumber ??
        "-",
    },
  ];
}

function buildSensorIdentifier(sensor, device, index = 0) {
  const baseName = sensorLabel(sensor, index);
  const sensorId = sensor?.sensorId ? ` (${sensor.sensorId})` : "";
  if (device?.deviceName) {
    return `${device.deviceName} • ${baseName}${sensorId}`;
  }
  return `${baseName}${sensorId}`;
}

function sensorLabel(sensor, index = 0) {
  if (!sensor) return `Sensor ${index + 1}`;
  if (sensor.sensorName) return sensor.sensorName;
  if (sensor.sensorPort) return `Sensor Port ${sensor.sensorPort}`;
  if (sensor.sensorId) return `Sensor ${sensor.sensorId}`;
  return `Sensor ${index + 1}`;
}

function sensorTypeLabel(sensorType) {
  return SENSOR_TYPE_LABELS[sensorType] || formatFallbackLabel(sensorType || "Unknown");
}

function sensorAccentColor(sensorType) {
  return SENSOR_TYPE_COLORS[sensorType] || SENSOR_TYPE_COLORS.default;
}

function formatFallbackLabel(value) {
  return String(value)
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatNumber(value, decimals = 1) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "-";
  return numeric.toFixed(decimals);
}

function valueWithUnit(value, unit, decimals = 1) {
  const formatted = formatNumber(value, decimals);
  return formatted === "-" ? "-" : `${formatted} ${unit}`;
}

function averageOf(values) {
  const numbers = values
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value));

  if (numbers.length === 0) return NaN;

  return numbers.reduce((sum, value) => sum + value, 0) / numbers.length;
}

function joinNames(values) {
  const names = values.filter(Boolean);
  return names.length > 0 ? names.join("\n") : "-";
}

const SUMMARY_GRID_STYLE = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
  gap: 16,
  marginBottom: "20px",
  marginTop: "20px",
};

const SUMMARY_TILE_STYLE = {
  wrap: (dark) => ({
    background: dark ? "#424242" : "#fff",
    borderRadius: 3,
    padding: "20px 16px 12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    position: "relative",
    minHeight: 92,
  }),
  big: (dark) => ({
    fontSize: 28,
    fontWeight: 600,
    lineHeight: 1,
    color: dark ? "#f3f3f3" : "#2c3e50",
    display: "flex",
    alignItems: "baseline",
    gap: 6,
  }),
  unit: (dark) => ({
    fontSize: 14,
    color: dark ? "#d1d5db" : "#6b7280",
    fontWeight: 500,
  }),
  label: (dark) => ({
    fontSize: 13,
    marginTop: 6,
    color: dark ? "#ccc" : "#7f8c8d",
  }),
  accent: {
    position: "absolute",
    left: 0,
    bottom: 0,
    height: 4,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    transition: "width .4s ease",
  },
};

const SENSOR_SUMMARY_SKELETON_TILES = [
  {
    key: "attached",
    color: "#2563eb",
    valueWidth: "42%",
    labelWidth: "54%",
  },
  {
    key: "temp-hum",
    color: "#10b981",
    valueWidth: "34%",
    labelWidth: "62%",
  },
  {
    key: "warmest",
    color: "#dc2626",
    valueWidth: "48%",
    labelWidth: "58%",
  },
  {
    key: "humidity",
    color: "#0ea5e9",
    valueWidth: "44%",
    labelWidth: "66%",
  },
];

const SENSOR_CARD_SKELETONS = [
  {
    key: "temp-hum",
    titleWidth: "42%",
    typeWidth: "18%",
    metricChips: [
      { color: "#dc2626", valueWidth: "56%", labelWidth: "42%" },
      { color: "#2563eb", valueWidth: "52%", labelWidth: "38%" },
      { color: "#0ea5e9", valueWidth: "48%", labelWidth: "46%" },
    ],
    readingRows: [
      { labelWidth: "44%", valueWidth: "36%" },
      { labelWidth: "44%", valueWidth: "34%" },
      { labelWidth: "30%", valueWidth: "32%" },
    ],
    detailRows: [
      { labelWidth: "32%", valueWidth: "42%" },
      { labelWidth: "30%", valueWidth: "36%" },
      { labelWidth: "18%", valueWidth: "26%" },
      { labelWidth: "24%", valueWidth: "40%" },
      { labelWidth: "46%", valueWidth: "34%" },
    ],
  },
];

const SENSOR_CARD_STYLE = {
  wrap: (dark) => ({
    background: dark ? "#424242" : "#fbfcfe",
    border: dark ? "1px solid rgba(148, 163, 184, 0.18)" : "1px solid #edf1f5",
    borderRadius: 8,
    padding: "20px 0 12px",
    boxShadow: dark ? "0 1px 4px rgba(0,0,0,0.18)" : "0 1px 4px rgba(15, 23, 42, 0.04)",
    marginBottom: "22px",
    overflow: "hidden",
  }),
  header: {
    padding: "0 24px",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
  },
  title: (dark) => ({
    fontSize: "22px",
    fontWeight: 700,
    color: dark ? "#f8fafc" : "#1f2937",
    lineHeight: 1.2,
    paddingLeft: "12px",
  }),
  metricRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 170px))",
    justifyContent: "start",
    gap: "12px",
    padding: "18px 24px 6px",
  },
};

const METRIC_CHIP_STYLE = {
  wrap: (dark) => ({
    minHeight: "108px",
    padding: "16px 14px 12px",
    borderRadius: 6,
    background: dark ? "rgba(55, 65, 81, 0.68)" : "#f8fafc",
    boxShadow: dark ? "inset 0 0 0 1px rgba(148, 163, 184, 0.14)" : "inset 0 0 0 1px #e5e7eb",
  }),
  value: (dark) => ({
    fontSize: "20px",
    fontWeight: 700,
    lineHeight: 1.1,
    color: dark ? "#f8fafc" : "#1f2937",
    display: "flex",
    alignItems: "baseline",
    gap: "6px",
  }),
  unit: (dark) => ({
    fontSize: "12px",
    fontWeight: 600,
    color: dark ? "#cbd5e1" : "#64748b",
  }),
  label: (dark) => ({
    fontSize: "12px",
    marginTop: "6px",
    color: dark ? "#cbd5e1" : "#64748b",
  }),
};

const SKELETON_DETAIL_STYLE = {
  panel: (dark) => ({
    borderRadius: 6,
    overflow: "hidden",
    boxShadow: dark
      ? "inset 0 0 0 1px rgba(148, 163, 184, 0.14)"
      : "inset 0 0 0 1px #e5e7eb",
  }),
  header: (dark) => ({
    padding: "12px 16px",
    background: dark ? "rgba(55, 65, 81, 0.52)" : "#f8fafc",
    borderBottom: dark
      ? "1px solid rgba(148, 163, 184, 0.12)"
      : "1px solid #e5e7eb",
  }),
  row: (dark, index) => ({
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 0.8fr)",
    alignItems: "center",
    gap: 20,
    padding: "12px 16px",
    background:
      index % 2 === 0
        ? dark
          ? "rgba(55, 65, 81, 0.3)"
          : "#fff"
        : dark
        ? "rgba(55, 65, 81, 0.5)"
        : "#f8fafc",
    borderTop:
      index === 0
        ? "none"
        : dark
        ? "1px solid rgba(148, 163, 184, 0.1)"
        : "1px solid #eef2f7",
  }),
};

const EMPTY_STATE_STYLE = {
  wrap: (dark) => ({
    background: dark ? "#424242" : "#fff",
    borderRadius: 8,
    padding: "28px 24px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  }),
  title: (dark) => ({
    fontSize: "22px",
    fontWeight: 700,
    color: dark ? "#f8fafc" : "#1f2937",
  }),
  description: (dark) => ({
    fontSize: "14px",
    lineHeight: 1.6,
    color: dark ? "#cbd5e1" : "#64748b",
    marginTop: "8px",
    maxWidth: "640px",
  }),
};
